// src/services/sbaService.js

import api from '../fapis/client';
import endpoints from '../fapis/endpoints';

// ============================================================
// HELPER: Format error message
// ============================================================
const formatErrorMessage = (error) => {
  if (error.message === 'Failed to fetch' || error.code === 'ERR_NETWORK') {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'The request timed out. Please try again.';
  }

  if (error.response) {
    const status = error.response.status;
    const detail = error.response.data?.detail || '';

    // 400 - Bad request (invalid token, expired, passwords don't match)
    if (status === 400) {
      if (detail.includes('expired')) {
        return 'This invitation link has expired. Please contact your administrator for a new one.';
      }
      if (detail.includes('Invalid or expired')) {
        return 'This invitation link is invalid. Please check your email or contact support.';
      }
      if (detail.includes('Password must be')) {
        return 'Password must be at least 8 characters long.';
      }
      if (detail.includes('Passwords do not match')) {
        return 'Passwords do not match. Please try again.';
      }
      if (detail.includes('Token is required')) {
        return 'Invitation token is missing. Please use the link from your email.';
      }
      if (detail.includes('Invalid domain')) {
        return 'Invalid domain. Please access this page from the correct company URL.';
      }
      return detail || 'Invalid request. Please check your input.';
    }

    // 403 - Forbidden (subdomain mismatch, company suspended)
    if (status === 403) {
      if (detail.includes('subdomain') || detail.includes('Invalid invite for this company')) {
        return 'This invitation is not valid for this company. Please check the URL or contact support.';
      }
      if (detail.includes('suspended')) {
        return 'This company account has been suspended. Please contact support for assistance.';
      }
      return detail || 'Access denied. You do not have permission to perform this action.';
    }

    // 404 - Not found (invalid token, company not found)
    if (status === 404) {
      if (detail.includes('Company not found')) {
        return 'Company not found. Please contact support.';
      }
      return detail || 'Invalid or expired invitation link.';
    }

    // 500 - Server errors
    if (status === 500) {
      if (detail.includes('role not found')) {
        return 'Configuration error. Please contact support.';
      }
      if (detail.includes('features')) {
        return 'Company setup is incomplete. Please contact support.';
      }
      if (detail.includes('permissions')) {
        return 'Company permissions are not configured. Please contact support.';
      }
      return 'Server error. Please try again later or contact support.';
    }

    return detail || error.response.data?.message || error.message;
  }

  return error.message || 'An unexpected error occurred. Please try again.';
};

// ============================================================
// SBA SERVICE - Super Admin Invites
// ============================================================
export const sbaService = {
  /**
   * Verify super admin invite token
   * @param {string} token - Invite token from URL
   * @returns {Promise<{success: boolean, data?: Object, message?: string, status?: number}>}
   */
  verifyInvite: async (token) => {
    if (!token) {
      return {
        success: false,
        message: 'No invitation token provided. Please check your invitation link.',
        status: 400,
        data: null
      };
    }

    try {
      const response = await api.get(endpoints.sba.verifyInvite(token));
      return {
        success: response.data?.success || false,
        data: response.data || null,
        status: response.status
      };
    } catch (error) {
      console.error('❌ Verify super admin invite error:', error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500,
        data: null
      };
    }
  },

  /**
   * Accept super admin invite and set password
   * @param {string} token - Invite token
   * @param {string} password - New password
   * @param {string} confirmPassword - Password confirmation
   * @param {string} fullName - User's full name
   * @param {string} phone - User's phone number
   * @returns {Promise<{success: boolean, data?: Object, message?: string, status?: number}>}
   */
  acceptInvite: async (token, password, confirmPassword, fullName = '', phone = '') => {
    // Client-side validation
    if (!token) {
      return {
        success: false,
        message: 'Invitation token is missing.',
        status: 400,
        data: null
      };
    }

    if (!password) {
      return {
        success: false,
        message: 'Password is required.',
        status: 400,
        data: null
      };
    }

    if (password !== confirmPassword) {
      return {
        success: false,
        message: 'Passwords do not match.',
        status: 400,
        data: null
      };
    }

    if (password.length < 8) {
      return {
        success: false,
        message: 'Password must be at least 8 characters long.',
        status: 400,
        data: null
      };
    }

    try {
      const response = await api.post(endpoints.sba.acceptInvite, {
        token,
        password,
        confirm_password: confirmPassword,
        full_name: fullName,
        phone
      });

      return {
        success: response.data?.success || false,
        data: response.data || null,
        message: response.data?.message || 'Account activated successfully',
        status: response.status
      };
    } catch (error) {
      console.error('❌ Accept super admin invite error:', error);
      return {
        success: false,
        message: formatErrorMessage(error),
        status: error.response?.status || error.status || 500,
        data: null
      };
    }
  }
};

export default sbaService;